import React, { useCallback, useMemo, useState } from 'react';
import { SourceItem } from "../../shared/models/source-item.model";
import { useSiteContext } from "../../state/SiteContext";
import { FilterActionName, useFilterAction, useFilterState, useSystemPropertyFilterStringValue } from "../../state/FilterContext";
import { TextExtractor, findFirstComponentOfType } from "../internal/TextExtractor";
import { ArrayProvider } from "../../state/ArrayContext";
import { BaseSelector, collectProps } from "../internal/BaseSelector";
import { CustomPropertyValuesQuery } from "../source-components/array-sources/CustomPropertyValuesQuery";
import { CustomPropertyFilterValues } from "../source-components/array-sources/CustomPropertyFilterValues";
import { useLocation } from 'react-router-dom';
import { useNavigate } from 'react-router-dom';
import { applyDefaults, getStateAsLocationQuery } from '../../state/state-url-management';
import { buildFilterRedirectNavigateTo, hasFilterRedirectNavigateIntent } from '../../shared/utils/filter-redirect-url.utils';
import { useToggleSwitchController } from '../../state/toggle-switch-controller.context';
import { activateToggleSwitchByName } from './toggle-switch-activation.utils';

interface CustomPropertySelectorProps {
  optionlabeltemplate: string,
  multiple: string,
  redirect?: string,
  redirectLayout?: string,
  activateToggleSwitch?: string,
  children: React.ReactNode,
}
/***
 * A selector component that allows the user to select a value from a list of values.
 * The list of values is loaded using an ArraySource specification.
 */
export const CustomPropertySelector = React.memo(function CustomPropertySelector({ optionlabeltemplate, multiple, redirect, redirectLayout = "", activateToggleSwitch, children }: CustomPropertySelectorProps) {
  const navigate = useNavigate();
  const location = useLocation();
  const setFilterState = useFilterAction(FilterActionName.SET_STATE);
  const hidFilter = useSystemPropertyFilterStringValue("hid");
  const filterState = useFilterState();
  const toggleSwitchController = useToggleSwitchController();

  // Get the label from the label child
  const labelChild = useMemo(() => findFirstComponentOfType(children, "arg-label"), [children]);
  const [labelValue, setLabelValue] = useState("");

  // Get the property name from the property child
  const propertyChild = useMemo(() => findFirstComponentOfType(children, "arg-property"), [children]);
  const [propertyName, setPropertyName] = useState("");

  const { siteState } = useSiteContext();

  // Get the autoCompleteProps from the autoCompleteProps child
  const autoCompletePropsChild = useMemo(() => findFirstComponentOfType(children, "arg-autocompleteprops"), [children]);
  const autoCompleteProps = useMemo(() => collectProps(autoCompletePropsChild), [autoCompletePropsChild]);

  // Get the textfieldProps from the textfieldProps child
  const textfieldPropsChild = useMemo(() => findFirstComponentOfType(children, "arg-textfieldprops"), [children]);
  const textfieldProps = useMemo(() => collectProps(textfieldPropsChild), [textfieldPropsChild]);

  const isMultiple: boolean = multiple === "true";

  // Get the valueset array from the valueset child
  const [valueSetArray, setValueSetArray] = useState<Array<SourceItem>>([]);
  const memoizedValueSetArray = useMemo(() => valueSetArray, [valueSetArray]);

  const valuesetChild = useMemo(() => findFirstComponentOfType(children, "arg-valueset"), [children]);

  // Get the initial values array from a CustomPropertFilter array source defined in the component output
  const [initialValuesArray, setInitialValuesArray] = useState<Array<SourceItem>>([]);
  const memoizedInitialValuesArray = useMemo(() => initialValuesArray, [initialValuesArray]);

  const onChangeFunc = useCallback((values: Array<SourceItem>) => {
    const intentsRedirectNavigate = hasFilterRedirectNavigateIntent(redirect, redirectLayout);
    let redirectToPath: string | null = null;
    if (intentsRedirectNavigate) {
      const filterStateCopy = { ...filterState };
      if (hidFilter) {
        delete filterStateCopy.systemProperties?.["hid"];
      }
      filterStateCopy.customProperties = filterStateCopy.customProperties || {};
      filterStateCopy.customProperties[propertyName] = values.map(item => item.value);
      if (siteState?.site) {
        applyDefaults(filterStateCopy, siteState?.site);
      }
      const searchParams = getStateAsLocationQuery(filterStateCopy);
      redirectToPath = buildFilterRedirectNavigateTo({
        effectiveSitePath: siteState.sitePath ?? "",
        redirect,
        redirectLayout,
        currentPathname: location.pathname,
        searchParamsString: searchParams.toString(),
        locationHash: location.hash,
        activeToggles: activateToggleSwitch,
      });
    }
    const nextState = { ...filterState };
    nextState.customProperties = { ...(nextState.customProperties || {}) };

    if (values.length) {
      nextState.customProperties[propertyName] = values.map(item => item.value);
    } else {
      delete nextState.customProperties[propertyName];
    }

    if (hidFilter) {
      const nextSystemProperties = { ...(nextState.systemProperties || {}) };
      delete nextSystemProperties["hid"];
      nextState.systemProperties = nextSystemProperties;
    }
    setFilterState(nextState);
    activateToggleSwitchByName(activateToggleSwitch, toggleSwitchController, "CustomPropertySelector");
    if (redirectToPath) {
      navigate(redirectToPath, { replace: true });
    }
  }, [
    activateToggleSwitch,
    setFilterState,
    navigate,
    propertyName,
    redirect,
    redirectLayout,
    siteState.sitePath,
    siteState.site,
    filterState,
    hidFilter,
    location.hash,
    location.pathname,
    toggleSwitchController,
  ]);

  const memoizedInitialStringValues = useMemo(() => {
    return memoizedInitialValuesArray.map(item => item.value.toString());
  }, [memoizedInitialValuesArray]);

  return <>
    <BaseSelector
      label={labelValue}
      valueset={memoizedValueSetArray}
      initialValues={memoizedInitialStringValues}
      optionlabeltemplate={optionlabeltemplate}
      autocompleteprops={autoCompleteProps}
      textfieldprops={textfieldProps}
      multiple={isMultiple}
      onChange={onChangeFunc}
    />

    <ArrayProvider setArray={setValueSetArray}>
      {valuesetChild?.props.children || <CustomPropertyValuesQuery propertyNameArg={propertyName} />}
    </ArrayProvider>
    <ArrayProvider setArray={setInitialValuesArray}>
      <CustomPropertyFilterValues propertyNameArg={propertyName} />
    </ArrayProvider>
    <TextExtractor onExtractedText={setLabelValue}>
      {labelChild?.props.children}
    </TextExtractor>
    <TextExtractor onExtractedText={setPropertyName}>
      {propertyChild?.props.children}
    </TextExtractor>
  </>
});